import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { MorphingOrb } from "@/components/PremiumAnimations";
import { caseStudies } from "@/data/caseStudies";
import SEOHead from "@/components/SEOHead";

export default function WorkPage() {
  return (
    <main data-testid="work-page">
      <SEOHead
        title="Our Work | Case Studies — Venury"
        description="Technical demonstrations of our engineering capabilities. Explore case studies in FinTech, trading platforms, AI/ML, blockchain, and SaaS development."
        url="https://venury.tech/work"
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <MorphingOrb
          color1="rgba(22, 160, 133, 0.12)"
          color2="rgba(72, 201, 176, 0.06)"
          size={500}
          duration={22}
          style={{ top: "-10%", right: "5%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16 w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em] mb-6"
          >
            Case Studies
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight max-w-4xl leading-[1.05]"
          >
            Our <span className="gradient-text-teal">Work</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            Technical demonstrations of our engineering capabilities. Production-grade architecture and code.
          </motion.p>
        </div>
      </section>

      {/* Stats Bar */}
      <section data-testid="work-stats" className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "10+", label: "Capabilities Showcased" },
              { value: "Full-Stack", label: "End-to-End Coverage" },
              { value: "8-16wk", label: "Delivery Timeline" },
              { value: "100%", label: "Code Ownership" },
            ].map((stat, i) => (
              <div key={i} data-testid={`work-stat-${i}`}>
                <span className="font-mono text-2xl md:text-3xl font-bold text-teal-bright block">{stat.value}</span>
                <span className="font-mono text-xs text-slate-600 uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section data-testid="work-grid" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8" staggerDelay={0.12}>
            {caseStudies.map((project, i) => (
              <StaggerItem key={project.slug}>
                <Link
                  to={`/work/${project.slug}`}
                  data-testid={`work-card-${project.slug}`}
                  className="block glass-card gradient-border rounded-lg overflow-hidden group h-full flex flex-col"
                >
                  <div className="relative h-56 overflow-hidden flex-shrink-0">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] via-[#1a252f]/40 to-transparent" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded backdrop-blur-sm">
                        {project.category}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-wider bg-white/10 text-slate-400 px-3 py-1 rounded backdrop-blur-sm">
                        {project.tag}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-teal-bright transition-colors duration-200">
                      {project.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1">
                      {project.shortDesc}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-6">
                        {project.metrics.map((m, j) => (
                          <div key={j}>
                            <span className="font-mono text-lg font-bold text-teal-bright block">{m.value}</span>
                            <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">{m.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-teal-bright group-hover:bg-teal-primary/10 transition-colors duration-200">
                        <ArrowUpRight size={16} className="text-slate-600 group-hover:text-teal-bright transition-colors duration-200" />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-white/5">
                      {project.tags.map((tag) => (
                        <span key={tag} className="font-mono text-[10px] px-2 py-1 rounded bg-white/5 text-slate-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </main>
  );
}
